import { forwardRef } from 'react'
import { User } from '../../lib/user'
import { GameStatus, Actions } from './Game'
import { Settings } from '../../lib/settings'
import {
  GameMode,
  GAME_MODE_UNLIMITED,
  GAME_MODE_DAILY,
  GAME_MODE_MINI_DAILY,
  GAME_MODE_CUSTOM,
  GAME_MODE_ACHIEVEMENTS,
  SEARCH_PARAM_UNLIMITED,
  GAME_MODE_TIPS
} from '../../constants/strings'
import { DAILY_LIST, MINI_DAILY_LIST } from '../../lib/words'
import { GameStats } from '../../lib/localStorage'
import { DailyGame } from './DailyGame'
import { UnlimitedGame } from './UnlimitedGame'
import { CustomGame } from './CustomGame'
import { Tips } from '../static/Tips'
import { AchievementsPage } from '../static/AchievementsPage'
import { useSearchParams } from 'react-router-dom'

type Props = {
  mode: GameMode,
  puzzleId: string,
  index: number|null,
  seed: string,
  user: User,
  settings: Settings,
  actions: Actions,
  setIsStatsModalOpen: (isOpen: boolean) => void
  onGameStatusChange: (status: GameStatus) => void
  onUpdateStats: (stats: GameStats) => void
  onOpenArchive: () => void
  onHint: () => void
  setResponsive: (isResponsive: boolean) => void
}

export const GameModeSwitch = forwardRef(({
  mode,
  puzzleId,
  index,
  seed,
  user,
  settings,
  actions,
  setIsStatsModalOpen,
  onGameStatusChange,
  onUpdateStats,
  onOpenArchive,
  onHint,
  setResponsive,
}: Props, ref) => {
  const searchParams = useSearchParams()[0]
  if (searchParams.has(SEARCH_PARAM_UNLIMITED)) {
    // don't render a partial daily game before we reroute
    return <></>
  }
  if (mode === GAME_MODE_DAILY || mode === GAME_MODE_MINI_DAILY) {
    const list = mode === GAME_MODE_DAILY ? DAILY_LIST : MINI_DAILY_LIST
    return (
      <DailyGame
        ref={ref}
        mode={mode}
        list={list}
        user={user}
        index={index}
        settings={settings}
        actions={actions}
        setIsStatsModalOpen={setIsStatsModalOpen}
        onGameStatusChange={onGameStatusChange}
        onUpdateStats={onUpdateStats}
        onOpenArchive={onOpenArchive}
        onHint={onHint}
        setResponsive={setResponsive}
      />
    )
  }
  if (mode === GAME_MODE_UNLIMITED) {
    return (
      <UnlimitedGame
        ref={ref}
        list={DAILY_LIST}
        user={user}
        seed={seed!}
        puzzleId={puzzleId!}
        settings={settings}
        actions={actions}
        setIsStatsModalOpen={setIsStatsModalOpen}
        onGameStatusChange={onGameStatusChange}
        onUpdateStats={onUpdateStats}
        onHint={onHint}
        setResponsive={setResponsive}
      />
    )
  }
  if (mode === GAME_MODE_CUSTOM) {
    return (
      <CustomGame
        ref={ref}
        user={user}
        seed={seed!}
        puzzleId={puzzleId!}
        settings={settings}
        actions={actions}
        setIsStatsModalOpen={setIsStatsModalOpen}
        onGameStatusChange={onGameStatusChange}
        onUpdateStats={onUpdateStats}
        onHint={onHint}
        setResponsive={setResponsive}
      />
    )
  }
  if (mode === GAME_MODE_TIPS) {
    return <Tips user={user} isDarkMode={settings.isDarkMode} />
  }

  if (mode === GAME_MODE_ACHIEVEMENTS) {
    return <AchievementsPage user={user}  />
  }
  return <></>
})
